/**
 * cart-upsell.js
 *
 * <cart-upsell-recommendations> custom element
 *
 * Cart refresh strategy
 * ─────────────────────
 * cart-items-component listens for ThemeEvents.cartUpdate on document.
 * That string is private to the theme bundle (@theme/events). We discover
 * it at runtime using a document.addEventListener patch that stays active
 * until it captures the first 'theme:…cart…' type, then self-removes.
 *
 * Once we know the type, dispatching a CustomEvent with the correct
 * detail shape ({ data: { sections: { [sectionId]: html } } }) causes
 * cart-items-component to call morphSection and update the drawer in-place.
 *
 * If the sniffer never captures a string (script loaded after all components
 * connected), we fall back to directly replacing the component's innerHTML
 * from the section HTML returned by /cart/add.js.
 */

// ─── Event type sniffer ──────────────────────────────────────────────────────
// Intercept document.addEventListener synchronously. Stays active until the
// first matching event type is captured, then restores the original method.
// Must run before cart-items-component's connectedCallback fires.

let _capturedCartEventType = null;
const _origAddEventListener = document.addEventListener.bind(document);

document.addEventListener = function (type, listener, options) {
  if (
    !_capturedCartEventType &&
    typeof type === 'string' &&
    type.startsWith('theme:') &&
    type.toLowerCase().includes('cart')
  ) {
    _capturedCartEventType = type;
    // Restore immediately so nothing else is affected
    document.addEventListener = _origAddEventListener;
  }
  return _origAddEventListener(type, listener, options);
};

// ─── Custom element ──────────────────────────────────────────────────────────

class CartUpsellRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #lastProductId = null;

  connectedCallback() {
    // When the element is injected fresh into the DOM after a 0→1 cart add,
    // connectedCallback fires before the browser has laid out the drawer and
    // before the morph has fully settled dataset attributes. Two rAF ticks
    // are enough for paint + dataset to be ready; the 50 ms fallback covers
    // slower devices. The IntersectionObserver remains as the primary trigger
    // when the element was already in the DOM before the drawer opened.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#load();
      });
    });
    this.#intersectionObserver.observe(this);
  }

  // Public escape hatch for external callers (e.g. after a cart mutation updates dataset)
  _triggerLoad(force = false) {
    if (force) {
      this.#lastProductId = null;
      delete this.dataset.recommendationsPerformed;
    }
    this.#load();
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }

  // ── Recommendations ────────────────────────────────────────────────────────

  async #load() {
    const { productId, fetchLimit = 10, limit = 4 } = this.dataset;

    if (!productId) return;
    if (this.dataset.recommendationsPerformed === 'true') return;
    if (this.#lastProductId === productId) return;
    this.#lastProductId = productId;

    const cartIds = new Set(
      (this.dataset.cartProductIds || '').split(',').map((id) => id.trim()).filter(Boolean)
    );

    const isWholesale = this.dataset.isWholesale === 'true';

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(
        // Fetch the full pool before excluding wholesale-only and already-added items.
        `/recommendations/products.json?product_id=${productId}&limit=10&intent=related`,
        { signal: controller.signal }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const { products } = await res.json();
      const filtered = (products ?? [])
        .filter((p) => !cartIds.has(String(p.id)))
        .filter((p) => isWholesale || !this.#isB2BOnly(p))
        .slice(0, Number(limit));

      if (!filtered.length) { this.#hide(); return; }

      const aspectRatio  = this.dataset.aspectRatio || '1 / 1';
      const cornerRadius = getComputedStyle(this)
        .getPropertyValue('--resource-card-corner-radius').trim() || '0px';

      const list = document.createElement('ul');
      list.className = 'cart-upsell__list list-unstyled';
      filtered.forEach((p) => list.appendChild(this.#buildCard(p, aspectRatio, cornerRadius)));

      this.innerHTML = '';
      this.appendChild(list);
      this.dataset.recommendationsPerformed = 'true';
    } catch (err) {
      console.error('[cart-upsell] Recommendations fetch failed:', err.message);
      this.#lastProductId = null;
      this.#hide();
    } finally {
      clearTimeout(timeout);
    }
  }

  // ── B2B / wholesale-only filtering ─────────────────────────────────────────

  /**
   * Shopify's native /recommendations/products.json endpoint has no concept
   * of this store's B2B/B2C visibility rules — it will happily recommend a
   * product tagged "b2b-only" (SparkLayer wholesale-exclusive) to a regular
   * B2C shopper. Mirrors the `section_visibility` pattern used elsewhere in
   * the theme (e.g. sections/rich-text.liquid), which checks
   * customer.metafields.sparklayer.authentication before showing B2B-only
   * content. Here, is-wholesale is passed in from Liquid via a data
   * attribute, and we drop any recommended product tagged "b2b-only" unless
   * the current customer is wholesale-authenticated.
   */
  #isB2BOnly(product) {
    const rawTags = product?.tags;
    if (!rawTags) return false;
    const tagList = Array.isArray(rawTags) ? rawTags : String(rawTags).split(',');
    return tagList.some((tag) => String(tag).trim().toLowerCase() === 'b2b-only');
  }

  // ── Card construction ──────────────────────────────────────────────────────

  /**
   * Builds a horizontal row card matching the design:
   *
   *  ┌──────┬─────────────────────┬──────────┐
   *  │ img  │ Title               │ £127.00  │
   *  │      │ [Add +]             │ ~~£177~~ │
   *  └──────┴─────────────────────┴──────────┘
   */
  #buildCard(product, aspectRatio, cornerRadius) {
    const li = document.createElement('li');
    li.className = 'cart-upsell__item';

    // ── Thumbnail ────────────────────────────────────────────
    const imgSrc    = product.featured_image ? this.#cdnUrl(product.featured_image, 300) : null;
    const imgSrcset = product.featured_image
      ? [150, 300, 600].map((w) => `${this.#cdnUrl(product.featured_image, w)} ${w}w`).join(', ')
      : null;

    const imageHtml = imgSrc
      ? `<img class="cart-upsell__card-image"
              src="${this.#esc(imgSrc)}"
              srcset="${this.#esc(imgSrcset)}"
              sizes="80px"
              alt="${this.#esc(product.title)}"
              loading="lazy" width="80" height="80" />`
      : `<div class="cart-upsell__card-image-placeholder" aria-hidden="true"></div>`;

    const thumbLink = document.createElement('a');
    thumbLink.href      = product.url;
    thumbLink.className = 'cart-upsell__card-thumb';
    thumbLink.setAttribute('tabindex', '-1');
    thumbLink.setAttribute('aria-hidden', 'true');
    thumbLink.innerHTML = imageHtml;

    // ── Middle: title + CTA button ───────────────────────────
    const middle = document.createElement('div');
    middle.className = 'cart-upsell__card-middle';

    const titleLink = document.createElement('a');
    titleLink.href        = product.url;
    titleLink.className   = 'cart-upsell__card-title';
    titleLink.textContent = product.title;

    middle.appendChild(titleLink);
    middle.appendChild(this.#buildAtcButton(product));

    // ── Right: price block ───────────────────────────────────
    const priceBlock = document.createElement('div');
    priceBlock.className = 'cart-upsell__card-price-block';

    // Determine prices — use the first variant for compare-at
    const firstVariant = (product.variants ?? [])[0];
    const compareAt    = firstVariant?.compare_at_price ?? 0;
    const currentPrice = product.price_varies ? product.price_min : product.price;
    const hasDiscount  = compareAt > currentPrice;

    priceBlock.innerHTML = `
      <span class="cart-upsell__card-price${hasDiscount ? ' cart-upsell__card-price--sale' : ''}">
        ${this.#money(currentPrice)}
      </span>
      ${hasDiscount
        ? `<s class="cart-upsell__card-price-compare">${this.#money(compareAt)}</s>`
        : ''}
    `;

    li.appendChild(thumbLink);
    li.appendChild(middle);
    li.appendChild(priceBlock);
    return li;
  }

  #buildAtcButton(product) {
    const variants = product.variants ?? [];

    // Always use the first available variant, regardless of variant count.
    // If none are available, fall back to the first variant for the sold-out state.
    const targetVariant = variants.find((v) => v.available) ?? variants[0];

    const btn = document.createElement('button');
    btn.type      = 'button';
    btn.className = 'cart-upsell__atc';

    if (!targetVariant?.available) {
      btn.disabled    = true;
      btn.textContent = 'Sold out';
      return btn;
    }

    btn.innerHTML         = 'Add <span class="cart-upsell__atc-plus" aria-hidden="true">+</span>';
    btn.dataset.variantId = targetVariant.id;
    btn.addEventListener('click', () => this.#addToCart(btn, targetVariant.id));
    return btn;
  }

  // ── Add to cart ────────────────────────────────────────────────────────────

  async #addToCart(btn, variantId) {
    btn.innerHTML = '';
    btn.setAttribute('data-loading', '');
    btn.disabled = true;

    try {
      const cartItemsEl = document.querySelector('cart-items-component[data-section-id]');
      const sectionId   = cartItemsEl?.dataset.sectionId ?? '';

      const response = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id:       variantId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // ── Refresh the full cart drawer in one shot ───────────────────────────
      // #refreshCartDrawer fetches /cart.js and re-renders items, total,
      // count badge, free shipping bar, and upsell widget together.
      await this.#refreshCartDrawer();

      // ── Stay permanently in Added state ───────────────────────────────────
      btn.removeAttribute('data-loading');
      btn.setAttribute('data-added', '');
      btn.textContent = '✔ Added';
      btn.disabled    = true;

    } catch (err) {
      console.error('[cart-upsell] Add to cart failed:', err.message);
      btn.removeAttribute('data-loading');
      btn.innerHTML = 'Add <span class="cart-upsell__atc-plus" aria-hidden="true">+</span>';
      btn.disabled  = false;
    }
  }

  // ── Checkout total updater ─────────────────────────────────────────────────  

  /**
   * Refreshes the cart drawer after a successful add-to-cart.
   *
   * Tries two approaches in order:
   *
   * 1. Dispatch the theme's internal cartUpdate event (type sniffed at startup).
   *    cart-items-component#handleCartUpdate receives it and calls morphSection,
   *    which does a fine-grained DOM diff of the cart items list.
   *
   * 2. If the event type was never captured (edge case: this module evaluated
   *    after all cart components had already connected), parse the section HTML
   *    ourselves and replace the cart-items-component's children directly.
   *    This is equivalent to what morphSection does for a full replacement.
   */
  
  async #refreshCartDrawer() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) return;
      const cart = await res.json();

      // ── Re-render cart line items using the same template Udesly uses ──
      const list = document.querySelector('[udy-el="items-list"]');
      if (list && window.udeslyMiniCartTemplate) {
        list.innerHTML = (cart.items || [])
          .map((item, index) => window.udeslyMiniCartTemplate({
            ...item,
            line: index + 1,
            remove: '/cart/change?line=' + (index + 1) + '&quantity=0',
          }))
          .join('');

        // Re-attach Udesly's quantity/remove listeners (same event Udesly fires internally)
        document.dispatchEvent(new CustomEvent('udesly:cart:rendered', { bubbles: true }));
      }

      // ── Update item count badge ──
      document.querySelectorAll('[udy-el="cart-count"]').forEach((el) => {
        el.textContent = cart.item_count;
      });

      // ── Update checkout total ──
      const formatted = this.#money(cart.total_price);
      document.querySelectorAll('[udy-el="cart-total"]').forEach((el) => {
        el.textContent = ` ${formatted}`;
      });

      // ── Update free shipping bar and upsell widget ──
      if (typeof window.updateFreeShippingBar === 'function') {
        window.updateFreeShippingBar(cart);
      }
      if (typeof window.updateCartUpsell === 'function') {
        window.updateCartUpsell(cart);
      }

    } catch (err) {
      console.error('[cart-upsell] Cart refresh failed:', err.message);
    }
  }

  // ── Utilities ──────────────────────────────────────────────────────────────

  #cdnUrl(src, width) {
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src);
      url.searchParams.set('width', width);
      return url.toString();
    } catch { return src; }
  }

  #money(cents) {
    return `£${(cents / 100).toFixed(2)}`;
  }

  #esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  #hide() {
    const wrapper = this.closest('.cart-upsell');
    if (wrapper) wrapper.hidden = true;
  }
}

if (!customElements.get('cart-upsell-recommendations')) {
  customElements.define('cart-upsell-recommendations', CartUpsellRecommendations);
}
